import { action, atom, reaction } from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'
import { loadPoolReatom } from '@root/features/services'

import type { WalletSwapInputModel } from './reatom-wallet-swap-input'
import type { PairModel } from '@root/entities/pair'

export const reatomWalletSwapPair = (swapInputReatom: WalletSwapInputModel) => {
  const { inCurrencyAtom, outCurrencyAtom } = swapInputReatom
  const { loadPoolsAction, poolsStateAtom, resetPoolAction } = loadPoolReatom()

  const selectedPoolAtom = atom<PairModel | undefined>(
    undefined,
    'selectedPoolAtom',
  )

  // Currencies order doesn't matter, the pool will be the same
  const currenciesKey = atom((ctx) => {
    const inCurrency = ctx.spy(inCurrencyAtom)?.address
    const outCurrency = ctx.spy(outCurrencyAtom)?.address

    const key = [inCurrency, outCurrency].sort()
    return key.join('-')
  }, 'currenciesKey')

  const currenciesChangeReaction = reaction((ctx) => {
    const _key = ctx.spy(currenciesKey)
    const inCurrency = ctx.get(inCurrencyAtom)
    const outCurrency = ctx.get(outCurrencyAtom)

    resetPoolAction(ctx)
    selectedPoolAtom(ctx, undefined)

    if (!inCurrency || !outCurrency) {
      return
    }

    loadPoolsAction(ctx, inCurrency, outCurrency)
  }, 'currenciesChangeReaction')

  const retryLoadPoolsAction = action((ctx) => {
    const inCurrency = ctx.get(inCurrencyAtom)
    const outCurrency = ctx.get(outCurrencyAtom)
    if (!inCurrency || !outCurrency) {
      return
    }

    loadPoolsAction(ctx, inCurrency, outCurrency)
  }, 'retryLoadPoolsAction')

  const selectPoolAction = action((ctx, pool: PairModel) => {
    selectedPoolAtom(ctx, pool)
  }, 'selectPoolAction')

  const poolsStateReaction = reaction((ctx) => {
    const poolsState = ctx.spy(poolsStateAtom)

    if (AsyncStateN.isSuccess(poolsState) && poolsState.value.length === 1) {
      const pool = poolsState.value.at(0)!
      selectPoolAction(ctx, pool)
      return
    }
  }, 'poolsStateReaction')

  return {
    selectPoolAction,
    selectedPoolAtom,
    currenciesChangeReaction,
    poolsStateAtom,
    poolsStateReaction,
    retryLoadPoolsAction,
  }
}

export type WalletSwapPairModel = ReturnType<typeof reatomWalletSwapPair>
